// Story 11.12 (ADO-1963), T3: the WOW add's in-flight state for one product on one page.
//
// One write at a time per product: a second press while one runs (a double click) does nothing.
// The outcome is kept for the product and WOW cart it was made for, so it is dropped when either
// changes. After an add that wrote, or may have written, or whose WOW cart was refused, the
// ordering choices are read again through the existing background reload
// (`reloadOrderingChoices`, which never changes the active entry): for the open-cart counts, and
// so a WOW entry no longer offered drops the page's WOW context. After a refused, forbidden or
// uncertain add, `onRefusedOrUncertain` re-reads the offer, and only while the page still shows
// the product and WOW cart the add was made for.

import { useCallback, useEffect, useRef, useState } from 'react';
import { cartTargetKey } from '../api/cart-target';
import { reloadOrderingChoices } from '../ordering/orderingSession';
import type { WowOfferContext } from '../pricing/useWowOffer';
import {
  addOneMoreToWow,
  refreshesTheChoices,
  rereadsTheOffer,
  startWowAdd,
  type StillCurrent,
  type WowAddResult,
} from './addToWowOrder';

export type WowAddPhase =
  | { readonly phase: 'idle' }
  | { readonly phase: 'busy' }
  | { readonly phase: 'settled'; readonly result: WowAddResult };

export interface WowOrderAdd {
  readonly phase: WowAddPhase;
  /** Act on the offer: add 1, or report the line already there. */
  readonly start: () => void;
  /** "Add 1 more" to the line reported by `start`. */
  readonly addOneMore: (token: string, revision: number) => void;
}

const IDLE: WowAddPhase = { phase: 'idle' };

export function useWowOrderAdd(
  sku: string,
  context: WowOfferContext | null,
  onRefusedOrUncertain: () => void,
): WowOrderAdd {
  const key =
    context === null ? null : [sku, cartTargetKey(context.cart), context.sessionKey].join('\n');
  const cart = context?.cart ?? null;
  const [state, setState] = useState<{ key: string; phase: WowAddPhase } | null>(null);
  const inFlight = useRef(false);
  const currentKey = useRef<string | null>(key);

  // The offer's latest re-read, so a settled add never calls one captured for an older context.
  const rereadOffer = useRef(onRefusedOrUncertain);
  useEffect(() => {
    currentKey.current = key;
    rereadOffer.current = onRefusedOrUncertain;
  }, [key, onRefusedOrUncertain]);
  useEffect(
    () => () => {
      currentKey.current = null;
    },
    [],
  );

  const run = useCallback(
    (attempt: (stillCurrent: StillCurrent) => Promise<WowAddResult>) => {
      if (inFlight.current || key === null || cart === null) return;
      inFlight.current = true;
      const runKey = key;
      const stillCurrent = () => currentKey.current === runKey;
      setState({ key: runKey, phase: { phase: 'busy' } });
      void attempt(stillCurrent)
        .then((result) => {
          if (refreshesTheChoices(result)) void reloadOrderingChoices({ background: true });
          // A page that moved on (another product, account or session, or gone) keeps its own
          // read: re-reading here would abort it for an answer nobody shows.
          if (!stillCurrent()) return;
          if (rereadsTheOffer(result)) rereadOffer.current();
          setState({ key: runKey, phase: { phase: 'settled', result } });
        })
        .finally(() => {
          inFlight.current = false;
        });
    },
    [key, cart],
  );

  const start = useCallback(() => {
    if (cart !== null) run((stillCurrent) => startWowAdd(sku, cart, stillCurrent));
  }, [run, sku, cart]);
  const addOneMore = useCallback(
    (token: string, revision: number) => {
      if (cart !== null) {
        run((stillCurrent) => addOneMoreToWow(sku, cart, token, revision, stillCurrent));
      }
    },
    [run, sku, cart],
  );

  return { phase: state !== null && state.key === key ? state.phase : IDLE, start, addOneMore };
}
